import { motion } from "framer-motion";
import { DeviceMobile, ArrowSquareOut, Plus, DotsThreeVertical, ShareNetwork, ArrowLeft } from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";

const Install = () => {
  const navigate = useNavigate();
  const appUrl = window.location.origin;

  const copyLink = () => {
    navigator.clipboard.writeText(appUrl);
    alert("Link copiado!");
  };

  return (
    <div className="min-h-screen bg-[#FDFBF7]">
      {/* Header */}
      <header className="bg-white border-b border-[#E4DFD3] sticky top-0 z-50">
        <div className="max-w-4xl mx-auto px-4 py-4 flex items-center gap-4">
          <Button onClick={() => navigate("/")} variant="ghost" className="rounded-full text-[#115E59]" data-testid="back-home-btn">
            <ArrowLeft size={24} />
          </Button>
          <h1 className="text-xl font-black text-[#042F2E]">Instalar App</h1>
        </div>
      </header>

      <div className="max-w-4xl mx-auto px-4 py-10">
        {/* Hero */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-12"
        >
          <div className="inline-flex items-center justify-center w-20 h-20 bg-[#FF5A26] rounded-2xl mb-6 shadow-lg">
            <DeviceMobile size={40} weight="duotone" className="text-white" />
          </div>
          <h2 className="text-3xl sm:text-4xl font-black text-[#042F2E] mb-3">
            Baixe o MARABU OPEN CLUB
          </h2>
          <p className="text-lg text-[#115E59] max-w-xl mx-auto">
            Instale nosso app direto no seu celular. Sem loja, sem espera!
          </p>
        </motion.div>

        {/* QR Code */}
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.1 }}
          className="bg-white rounded-3xl p-8 sm:p-12 border border-[#E4DFD3] shadow-sm text-center mb-10 max-w-md mx-auto"
          data-testid="qr-code-section"
        >
          <p className="text-sm font-bold text-[#115E59] uppercase tracking-wider mb-6">
            Escaneie o QR Code
          </p>
          <div className="bg-white p-4 rounded-2xl border-4 border-[#FF5A26] inline-block mb-6">
            <img
              src="/qrcode-install.png"
              alt="QR Code para instalar o app"
              className="w-56 h-56 sm:w-64 sm:h-64"
              data-testid="install-qr-code"
            />
          </div>
          <p className="text-[#115E59] text-sm mb-4">
            Aponte a câmera do celular para o QR Code acima
          </p>

          {/* Direct link */}
          <div className="bg-[#F4F1EA] rounded-xl p-4">
            <p className="text-xs font-bold text-[#115E59] uppercase tracking-wider mb-2">Ou copie o link</p>
            <div className="flex gap-2 items-center">
              <input
                type="text"
                value={appUrl}
                readOnly
                className="flex-1 bg-white border border-[#E4DFD3] rounded-lg px-3 py-2 text-sm text-[#042F2E] font-mono truncate"
                data-testid="app-link-input"
              />
              <Button
                onClick={copyLink}
                className="bg-[#FF5A26] hover:bg-[#E04818] text-white rounded-lg px-4 shrink-0"
                data-testid="copy-link-btn"
              >
                Copiar
              </Button>
            </div>
          </div>
        </motion.div>

        {/* Instructions Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-12">
          {/* iPhone */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.2 }}
            className="bg-white rounded-2xl p-8 border border-[#E4DFD3] shadow-sm"
            data-testid="iphone-instructions"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-gray-800 to-gray-900 rounded-xl flex items-center justify-center">
                <svg width="20" height="24" viewBox="0 0 20 24" fill="white">
                  <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.53 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z"/>
                </svg>
              </div>
              <div>
                <h3 className="text-xl font-bold text-[#042F2E]">iPhone (iOS)</h3>
                <p className="text-xs text-[#115E59]">Safari</p>
              </div>
            </div>

            <div className="space-y-5">
              <div className="flex gap-4">
                <div className="w-8 h-8 bg-[#FF5A26] rounded-full flex items-center justify-center shrink-0 text-white font-bold text-sm">1</div>
                <div>
                  <p className="font-semibold text-[#042F2E]">Abra no Safari</p>
                  <p className="text-sm text-[#115E59]">Escaneie o QR code ou acesse o link acima usando o <strong>Safari</strong></p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="w-8 h-8 bg-[#FF5A26] rounded-full flex items-center justify-center shrink-0 text-white font-bold text-sm">2</div>
                <div>
                  <p className="font-semibold text-[#042F2E]">Toque em Compartilhar</p>
                  <p className="text-sm text-[#115E59]">Toque no botão <ShareNetwork size={16} weight="bold" className="inline text-[#0891B2]" /> (quadrado com seta para cima) na barra inferior</p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="w-8 h-8 bg-[#FF5A26] rounded-full flex items-center justify-center shrink-0 text-white font-bold text-sm">3</div>
                <div>
                  <p className="font-semibold text-[#042F2E]">Adicionar à Tela Inicial</p>
                  <p className="text-sm text-[#115E59]">Role para baixo e toque em <strong>"Adicionar à Tela de Início"</strong> <Plus size={14} weight="bold" className="inline text-[#0891B2]" /></p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="w-8 h-8 bg-[#10B981] rounded-full flex items-center justify-center shrink-0 text-white font-bold text-sm">4</div>
                <div>
                  <p className="font-semibold text-[#042F2E]">Pronto!</p>
                  <p className="text-sm text-[#115E59]">O app MARABU aparecerá na sua tela inicial como um app nativo</p>
                </div>
              </div>
            </div>
          </motion.div>

          {/* Android */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: 0.3 }}
            className="bg-white rounded-2xl p-8 border border-[#E4DFD3] shadow-sm"
            data-testid="android-instructions"
          >
            <div className="flex items-center gap-3 mb-6">
              <div className="w-12 h-12 bg-gradient-to-br from-green-500 to-green-600 rounded-xl flex items-center justify-center">
                <svg width="22" height="24" viewBox="0 0 22 24" fill="white">
                  <path d="M1 8.5c0-.83.67-1.5 1.5-1.5s1.5.67 1.5 1.5v7c0 .83-.67 1.5-1.5 1.5S1 16.33 1 15.5v-7zm17 0c0-.83.67-1.5 1.5-1.5s1.5.67 1.5 1.5v7c0 .83-.67 1.5-1.5 1.5S18 16.33 18 15.5v-7zM4 7.5V17c0 .55.45 1 1 1h1v3.5c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5V18h2v3.5c0 .83.67 1.5 1.5 1.5s1.5-.67 1.5-1.5V18h1c.55 0 1-.45 1-1V7.5H4zm11.8-4.3l1.3-1.3c.2-.2.2-.5 0-.7s-.5-.2-.7 0L15 2.6C14.1 2.2 13.1 2 12 2s-2.1.2-3 .6L7.6 1.2c-.2-.2-.5-.2-.7 0s-.2.5 0 .7l1.3 1.3C6.8 4.3 5.7 6 5.2 7.5h13.6c-.5-1.5-1.6-3.2-3-4.3zM9 5.5c-.4 0-.8-.3-.8-.8s.3-.8.8-.8.8.3.8.8-.4.8-.8.8zm4 0c-.4 0-.8-.3-.8-.8s.3-.8.8-.8.8.3.8.8-.4.8-.8.8z"/>
                </svg>
              </div>
              <div>
                <h3 className="text-xl font-bold text-[#042F2E]">Android</h3>
                <p className="text-xs text-[#115E59]">Chrome</p>
              </div>
            </div>

            <div className="space-y-5">
              <div className="flex gap-4">
                <div className="w-8 h-8 bg-[#FF5A26] rounded-full flex items-center justify-center shrink-0 text-white font-bold text-sm">1</div>
                <div>
                  <p className="font-semibold text-[#042F2E]">Abra no Chrome</p>
                  <p className="text-sm text-[#115E59]">Escaneie o QR code ou acesse o link acima usando o <strong>Chrome</strong></p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="w-8 h-8 bg-[#FF5A26] rounded-full flex items-center justify-center shrink-0 text-white font-bold text-sm">2</div>
                <div>
                  <p className="font-semibold text-[#042F2E]">Toque no menu</p>
                  <p className="text-sm text-[#115E59]">Toque nos <DotsThreeVertical size={16} weight="bold" className="inline text-[#0891B2]" /> três pontos no canto superior direito</p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="w-8 h-8 bg-[#FF5A26] rounded-full flex items-center justify-center shrink-0 text-white font-bold text-sm">3</div>
                <div>
                  <p className="font-semibold text-[#042F2E]">Instalar aplicativo</p>
                  <p className="text-sm text-[#115E59]">Toque em <strong>"Instalar aplicativo"</strong> ou <strong>"Adicionar à tela inicial"</strong> <ArrowSquareOut size={14} weight="bold" className="inline text-[#0891B2]" /></p>
                </div>
              </div>
              <div className="flex gap-4">
                <div className="w-8 h-8 bg-[#10B981] rounded-full flex items-center justify-center shrink-0 text-white font-bold text-sm">4</div>
                <div>
                  <p className="font-semibold text-[#042F2E]">Pronto!</p>
                  <p className="text-sm text-[#115E59]">O app MARABU será instalado e aparecerá na sua tela inicial</p>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Share section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.4 }}
          className="text-center"
        >
          <Button
            onClick={() => {
              const shareText = `Baixe o app do MARABU OPEN CLUB! ${appUrl}/instalar`;
              window.open(`https://wa.me/?text=${encodeURIComponent(shareText)}`, '_blank');
            }}
            className="bg-[#25D366] hover:bg-[#20BA5A] text-white font-bold rounded-full px-8 py-6 text-lg"
            data-testid="share-whatsapp-btn"
          >
            <ShareNetwork size={24} weight="bold" className="mr-3" />
            Compartilhar via WhatsApp
          </Button>
        </motion.div>
      </div>
    </div>
  );
};

export default Install;
